import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Checkbox,
  Link,
  Typography,
} from "@material-ui/core";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { useFetchConsentForProfile, useSubscribeToOffers, useUnsubscribeFromOffers } from "hooks";
import checkUserConsent from "utils/checkUserConsent";
import { setHasChosenCommunicationPreferences } from "features/user/userSlice";
import { CONSENT_STATUS } from "utils/constants";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "2rem",
    paddingTop: 0,
    margin: "2rem",
  },
  preferenceHeader: {
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    gap: "1rem",
    margin: "2rem 0",
    justifySelf: (props) => (props.isModal ? "center" : "initial"),
  },
  preferencesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  marketingSection: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  preferencesSaveButton: {
    alignSelf: "flex-start",
    minWidth: "200px",
    width: "100%",
  },
  privacySection: {
    marginTop: "0.5rem",
  },
}));

export default function CommunicationPreferences({
  heading = "My Preferences",
  onNotifOpen,
  isModal = false,
}) {
  const classes = useStyles({ isModal });
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile);
  const firebaseToken = useSelector((state) => state.user.firebaseToken);
  const { fetchConsentForProfile } = useFetchConsentForProfile();
  const { subscribeToOffers } = useSubscribeToOffers();
  const { unsubscribeFromOffers } = useUnsubscribeFromOffers();

  const [consentCheckbox, setConsentCheckbox] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchConsentStatus = async () => {
    if (firebaseToken && profile?.id) {
      setLoading(true);
      try {
        const consentStatus = await fetchConsentForProfile(firebaseToken, profile.id);
        setConsentCheckbox(checkUserConsent(consentStatus.subscribed));
        if (consentStatus.subscribed === CONSENT_STATUS.NOT_CAPTURED) {
          await unsubscribeFromOffers(firebaseToken, profile.id);
        }
        setLoading(false);
        return consentStatus;
      } catch (error) {
        setLoading(false);
        return null;
      }
    }
  };

  const handleUpdatePreferences = async () => {
    setLoading(true);
    try {
      let result;
      if (consentCheckbox) {
        result = await subscribeToOffers(firebaseToken, profile.id);
      } else {
        result = await unsubscribeFromOffers(firebaseToken, profile.id);
      }
      setLoading(false);

      if (result.success) {
        onNotifOpen("Preferences updated successfully!", { variant: "success" });
        dispatch(setHasChosenCommunicationPreferences(true));
        await fetchConsentStatus();
      } else {
        onNotifOpen("Failed to update preferences.", { variant: "error" });
      }
    } catch (error) {
      setLoading(false);
      onNotifOpen("Failed to update preferences.", { variant: "error" });
    }
  };

  useEffect(() => {
    if (firebaseToken && profile?.id) {
      fetchConsentStatus();
    }
    return () => {
      dispatch(setHasChosenCommunicationPreferences(true));
    };
  }, [firebaseToken, profile?.id]);

  return (
    <Box className={isModal ? classes.container : undefined}>
      <Box className={classes.preferenceHeader}>
        <Typography variant="h5" color="textPrimary">
          {heading}
        </Typography>
        {!isModal && (
          <Typography color="textPrimary">
            Update your communication preferences to let us know how we can contact you.
          </Typography>
        )}
      </Box>
      <Box className={classes.preferencesContainer}>
        <div className={classes.marketingSection}>
          <Typography variant="body2" color="textSecondary">
            Marketing communication
          </Typography>
          <Typography color="textPrimary">
            Receive updates on our latest offers and promotional events.
          </Typography>
        </div>
        {loading ? (
          <LoadingSpinner loading={loading} />
        ) : (
          <FormControlLabel
            control={
              <Checkbox
                checked={consentCheckbox}
                onChange={(e) => {
                  setConsentCheckbox(e.target.checked);
                }}
                color="primary"
              />
            }
            label={
              <Typography variant="body1" color="textPrimary">
                Email
              </Typography>
            }
          />
        )}

        <Divider />

        <div className={classes.privacySection}>
          <Typography color="textPrimary">
            We treat your personal data with care; please read our{" "}
            <Link
              href="https://mollies.com/privacy-page/"
              target="_blank"
              rel="noopener noreferrer"
              color="primary">
              Privacy Notice
            </Link>
            .
          </Typography>
        </div>

        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          className={classes.preferencesSaveButton}
          onClick={handleUpdatePreferences}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

CommunicationPreferences.propTypes = {
  onNotifOpen: PropTypes.func.isRequired,
  heading: PropTypes.string,
  isModal: PropTypes.bool,
};
